import React from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { PageTopNav } from "@/components/nav/PageTopNav";

// The record type is inferred from the API response. Adjust as needed.
interface RecordDetail {
  id: string;
  orgId: string;
  speciesId: string;
  note?: string | null;
  locationNote?: string | null;
  siteId?: string | null;
  dtm: string;
  lat?: number | null;
  lng?: number | null;
  count?: number | null;
  createdAt: string;
  updatedAt: string;
  species?: {
    id: string;
    scientificName: string;
    englishName: string;
  };
  organisation?: {
    id: string;
    name: string;
  };
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export function AdminRecordDetail() {
  const { recordId } = useParams();

  const {
    data: record,
    isLoading,
    error,
  } = useQuery<RecordDetail>({
    queryKey: ["record", recordId],
    queryFn: async () => {
      const res = await fetch(`/api/record/${recordId}`);
      if (!res.ok) throw new Error("Failed to fetch record");
      return res.json();
    },
    enabled: !!recordId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="p-8 text-center text-red-600">Failed to load record details.</div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4 md:px-0 py-8">
      <PageTopNav pageName={`Record: ${record.id}`} />
      <Card>
        <CardHeader>
          <CardTitle>Record Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium">Record ID:</span> <span className="font-mono text-xs">{record.id}</span>
              </div>
              <div>
                <span className="text-sm font-medium">Date/Time:</span> {formatDate(record.dtm)}
              </div>
              <div>
                <span className="text-sm font-medium">Organization:</span> {record.organisation?.name || record.orgId}
              </div>
              <div>
                <span className="text-sm font-medium">Species:</span> {record.species?.englishName || record.speciesId}
              </div>
              <div>
                <span className="text-sm font-medium">Count:</span> {record.count ?? <span className="italic text-gray-400">N/A</span>}
              </div>
              <div>
                <span className="text-sm font-medium">Site ID:</span> {record.siteId ?? <span className="italic text-gray-400">N/A</span>}
              </div>
              <div>
                <span className="text-sm font-medium">Latitude:</span> {record.lat ?? <span className="italic text-gray-400">N/A</span>}
              </div>
              <div>
                <span className="text-sm font-medium">Longitude:</span> {record.lng ?? <span className="italic text-gray-400">N/A</span>}
              </div>
            </div>
            {record.note && (
              <div>
                <span className="text-sm font-medium">Note:</span>
                <p className="mt-1 text-sm p-2 bg-muted rounded-md">{record.note}</p>
              </div>
            )}
            {record.locationNote && (
              <div>
                <span className="text-sm font-medium">Location Note:</span>
                <p className="mt-1 text-sm p-2 bg-muted rounded-md">{record.locationNote}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div>
                <span className="text-sm font-medium">Created:</span> {formatDate(record.createdAt)}
              </div>
              <div>
                <span className="text-sm font-medium">Last Updated:</span> {formatDate(record.updatedAt)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
